import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, Users, Layers, FolderKanban, Activity, Settings, BarChart3, Shield } from 'lucide-react';

const STORAGE_KEY = 'lstack_walkthrough_done';

// Each step optionally targets a real element (by CSS selector) and a route to
// navigate to, so the tour highlights the live UI instead of describing it.
const STEPS = [
  {
    icon: <span style={{ fontSize: 32 }}>👋</span>,
    title: 'Welcome to LatentStack',
    body: "A quick guided tour of your admin dashboard. I'll walk you through each section — use Next to continue, or skip anytime.",
    selector: null,
    path: null,
  },
  {
    icon: <Users className="w-7 h-7 text-orange" />,
    title: 'Users',
    body: 'Create and manage user accounts here — assign tiers and reset rate limits.',
    selector: 'a[href="/users"]',
    path: '/users',
  },
  {
    icon: <Layers className="w-7 h-7 text-orange" />,
    title: 'Tiers',
    body: 'Tiers define which models are available and what rate limits apply. Create tiers and assign them to users or teams.',
    selector: 'a[href="/tiers"]',
    path: '/tiers',
  },
  {
    icon: <FolderKanban className="w-7 h-7 text-orange" />,
    title: 'Teams',
    body: 'Teams bundle a tier with a set of members. Members pick their team in the client to use its allowed models.',
    selector: 'a[href="/projects"]',
    path: '/projects',
  },
  {
    icon: <Shield className="w-7 h-7 text-orange" />,
    title: 'Guardrails',
    body: 'Define regex content guardrails applied to every request through the gateway — block unwanted patterns before they reach a model.',
    selector: 'a[href="/guardrails"]',
    path: '/guardrails',
  },
  {
    icon: <BarChart3 className="w-7 h-7 text-orange" />,
    title: 'Usage Analytics',
    body: 'Cost breakdowns by model, provider, and time across all users — plus the heatmap and "Email me the report" button.',
    selector: 'a[href="/llm-stats"]',
    path: '/llm-stats',
  },
  {
    icon: <Activity className="w-7 h-7 text-orange" />,
    title: 'My Usage',
    body: 'Your personal usage — total spend, calls, and rate-limit consumption per model.',
    selector: 'a[href="/my-usage"]',
    path: '/my-usage',
  },
  {
    icon: <Settings className="w-7 h-7 text-orange" />,
    title: 'Settings',
    body: 'Manage your profile, API key, password, and relaunch this tour anytime from here.',
    selector: 'a[href="/account"]',
    path: '/account',
  },
  {
    icon: <span style={{ fontSize: 32 }}>🎉</span>,
    title: "You're all set!",
    body: 'That wraps up the tour. Explore each section from the sidebar — and relaunch this walkthrough anytime with the help (?) icon.',
    selector: null,
    path: null,
  },
];

const PAD = 8;          // spotlight padding around the target
const TOOLTIP_W = 340;  // tooltip width (px)

// Keep only steps whose target exists for this user (admin-only sidebar links
// are absent for regular users); steps without a selector (welcome/end) stay.
function activeSteps() {
  return STEPS.filter((s) => !s.selector || document.querySelector(s.selector));
}

export default function Walkthrough({ forceOpen = false, onClose }) {
  const navigate = useNavigate();
  const [steps, setSteps] = useState(STEPS);
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState(null);   // target element bounding rect (or null = centered)
  const rafRef = useRef(null);

  const current = steps[step] || steps[0];

  // Find the current step's target element and capture its position.
  const measure = useCallback(() => {
    const s = steps[step];
    if (!s || !s.selector) { setRect(null); return; }
    const el = document.querySelector(s.selector);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    setRect(el.getBoundingClientRect());
  }, [step, steps]);

  const open = useCallback(() => {
    setSteps(activeSteps());
    setStep(0);
    setVisible(true);
  }, []);

  useEffect(() => {
    if (forceOpen) { open(); return; }
    if (!localStorage.getItem(STORAGE_KEY)) open();
  }, [forceOpen, open]);

  // On each step: navigate to its route, then measure the target (after the
  // route/layout has painted). Re-measure on resize/scroll.
  useEffect(() => {
    if (!visible) return;
    if (current.path) navigate(current.path);
    cancelAnimationFrame(rafRef.current);
    // double rAF + small timeout lets the active nav state + page settle first
    const t = setTimeout(() => {
      rafRef.current = requestAnimationFrame(() => requestAnimationFrame(measure));
    }, 80);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearTimeout(t);
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step, visible, current, navigate, measure]);

  const close = (_done = false) => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
    setStep(0);
    onClose?.();
  };

  // keyboard: arrows + escape
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      else if (e.key === 'ArrowRight') setStep((s) => Math.min(steps.length - 1, s + 1));
      else if (e.key === 'ArrowLeft') setStep((s) => Math.max(0, s - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, steps.length]);

  if (!visible) return null;

  const isLast = step === steps.length - 1;
  const progress = ((step + 1) / steps.length) * 100;

  // Tooltip placement: beside the highlighted element, else centered.
  let tipStyle;
  if (rect) {
    const vh = window.innerHeight;
    let top = rect.top;
    top = Math.min(top, vh - 240);          // keep on-screen vertically
    top = Math.max(16, top);
    const right = rect.right + 16 + TOOLTIP_W;
    const left = right < window.innerWidth
      ? rect.right + 16                      // to the right of the target (sidebar)
      : rect.left - 16 - TOOLTIP_W;          // fall back to the left
    tipStyle = { position: 'fixed', top, left: Math.max(16, left), width: TOOLTIP_W, zIndex: 10002 };
  } else {
    tipStyle = {
      position: 'fixed', width: TOOLTIP_W, zIndex: 10002,
      top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    };
  }

  return (
    <>
      {/* Spotlight: a hole over the target with a giant box-shadow dimming the rest */}
      {rect ? (
        <div
          style={{
            position: 'fixed',
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 10,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
            border: '2px solid #f97316',
            zIndex: 10001,
            pointerEvents: 'none',
            transition: 'all 0.25s ease',
          }}
        />
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 10001, pointerEvents: 'none' }} />
      )}

      {/* Tooltip / coachmark */}
      <div
        style={tipStyle}
        className="bg-panel border border-panel-border rounded-2xl shadow-2xl shadow-black/40 overflow-hidden"
      >
        {/* progress bar */}
        <div className="h-0.5 bg-field">
          <div className="h-full bg-orange transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>

        <div className="px-5 pt-4 pb-2 flex items-center justify-between">
          <span className="text-[11px] text-fg-muted font-mono">Step {step + 1} of {steps.length}</span>
          <button onClick={() => close(false)} className="text-fg-muted hover:text-fg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-4">
          <div className="flex items-center gap-2.5 mb-2">
            {current.icon}
            <h2 className="text-base font-bold text-fg">{current.title}</h2>
          </div>
          <p className="text-fg-muted text-sm leading-relaxed">{current.body}</p>
        </div>

        <div className="flex items-center justify-between px-5 pb-4 gap-3">
          <button onClick={() => close(false)} className="text-xs text-fg-muted hover:text-fg transition-colors">
            Skip tour
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-1 px-3 py-1.5 bg-panel border border-panel-border text-fg rounded-lg text-sm hover:border-orange transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />Back
              </button>
            )}
            {isLast ? (
              <button
                onClick={() => close(true)}
                className="flex items-center gap-1 px-4 py-1.5 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm font-medium"
              >
                Done
              </button>
            ) : (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="flex items-center gap-1 px-4 py-1.5 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm font-medium"
              >
                Next<ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
